import { Location } from "@/resources/types";

export type LocationOccupancyState = {
  schemaVersion: 1;
  counts: Record<string, number>;
};

export type LocationSelectOption = {
  label: string;
  value: string;
  disabled: boolean;
  atCapacity: boolean;
};

export const getOccupiedCount = (locationId: string, counts: Record<string, number> = {}) => counts[locationId] ?? 0;

export const isLocationAtCapacity = (location: Location, counts: Record<string, number> = {}) => {
  if (location.capacity == null) return false;
  return getOccupiedCount(location.id, counts) >= location.capacity;
};

export const buildLocationSelectOptions = (
  locations: Location[],
  {
    isEditMode,
    currentLocationId,
    showOccupiedLocations,
    occupancyCounts = {},
    applyCapacityFilter,
  }: {
    isEditMode: boolean;
    currentLocationId?: string;
    showOccupiedLocations: boolean;
    occupancyCounts?: Record<string, number>;
    applyCapacityFilter: boolean;
  },
) => {
  const passesCapacityFilter = (location: Location) => {
    if (!applyCapacityFilter || showOccupiedLocations) return true;
    if (isEditMode && location.id === currentLocationId) return true;
    return !isLocationAtCapacity(location, occupancyCounts);
  };

  const toSelectOption = (location: Location, disabled = false): LocationSelectOption => ({
    label: location.name,
    value: location.id,
    disabled,
    atCapacity: applyCapacityFilter && isLocationAtCapacity(location, occupancyCounts),
  });

  if (!isEditMode) {
    return locations
      .filter((location) => location.status === "active")
      .filter(passesCapacityFilter)
      .map((location) => toSelectOption(location));
  }

  const activeOptions = locations
    .filter((location) => location.status === "active")
    .filter(passesCapacityFilter)
    .map((location) => toSelectOption(location));

  const currentLocation = locations.find((location) => location.id === currentLocationId);

  if (currentLocation && currentLocation.status !== "active") {
    return [
      ...activeOptions,
      toSelectOption(currentLocation, true),
    ];
  }

  return activeOptions;
};
